import isEqual from 'lodash.isequal'

import { TransportTypeEnum } from '../../../lib/types/enums/trip/trip-type.enum'
import { TripStepTypeEnum } from '../../../lib/types/enums/trip-bookings/trip-step-type.enum'
import { CenterActivity } from '../../../lib/types/models/center-activity/center-activity'
import { CenterActivityInput } from '../../../lib/types/models/center-activity/center-activity-input'
import { CenterCourse } from '../../../lib/types/models/center-course/center-course'
import { CenterCourseInput } from '../../../lib/types/models/center-course/center-course-input'
import { CoachCompany } from '../../../lib/types/models/ol/coach-company/coach-company'
import { TripBookingTypes } from '../../../lib/types/models/trip-booking/trip-booking'
import { TripStepTypes } from '../../../lib/types/models/trip-booking/trip-step'

// Helper function to check if two TripBookingTypes.View objects are equal based ONLY ON data shown in the details page
export const isEqualData = (a: TripBookingTypes.View, b: TripBookingTypes.View) => {
  if (a.directionality !== b.directionality) return false

  const normalize = (booking: TripBookingTypes.View) => ({
    directionality: booking.directionality,
    travelCompany: booking.travelCompany
      ? { id: booking.travelCompany.id, name: booking.travelCompany.name }
      : null,
    transportMeans: booking.transportMeans ? { picto: booking.transportMeans.picto } : null,
    departureCoachCompany: booking.departureCoachCompany
      ? {
          name: booking.departureCoachCompany.name,
          phoneNumber: booking.departureCoachCompany.phoneNumber,
          emergencyNumber: booking.departureCoachCompany.emergencyNumber
        }
      : null,
    arrivalCoachCompany: booking.arrivalCoachCompany
      ? {
          name: booking.arrivalCoachCompany.name,
          phoneNumber: booking.arrivalCoachCompany.phoneNumber,
          emergencyNumber: booking.arrivalCoachCompany.emergencyNumber
        }
      : null,
    tripSteps: booking.tripSteps.map((step) => ({
      directionality: step.directionality,
      departureDate: step.departureDate,
      departurePlaceLabel: step.departurePlace?.label,
      departureTime: step.departureTime,
      transportMeanNumber: step.transportMeanNumber,
      arrivalDate: step.arrivalDate,
      arrivalPlaceLabel: step.arrivalPlace?.label,
      arrivalTime: step.arrivalTime,
      foreignProviderAppointmentPlace: step.foreignProviderAppointmentPlace
        ? {
            address1: step.foreignProviderAppointmentPlace.address1,
            address2: step.foreignProviderAppointmentPlace.address2,
            city: step.foreignProviderAppointmentPlace.city,
            country: step.foreignProviderAppointmentPlace.country,
            zipCode: step.foreignProviderAppointmentPlace.zipCode
          }
        : null,
      foreignProviderAppointmentTime: step.foreignProviderAppointmentTime,
      timeBeforeDepartureForGroup: step.timeBeforeDepartureForGroup,
      tripProviderGuidelineContent: step.tripProviderGuidelineContent
    }))
  })

  return isEqual(normalize(a), normalize(b))
}

//removes duplicates from an array of TripStepTypes.View
export const removeDuplicatesFromTripSteps = (bookings: TripBookingTypes.View[]) => {
  return bookings.filter(
    (booking, index, self) => index === self.findIndex((other) => isEqualData(booking, other))
  )
}

export const getTripBookingStepsAsBookings = (
  bookings: TripBookingTypes.View[]
): TripBookingTypes.View[] => {
  const wrapStep = (
    booking: TripBookingTypes.View,
    step: TripStepTypes.Light,
    coachCompany?: CoachCompany
  ): TripBookingTypes.View => {
    const { id, travelCompany, directionality, transportMeans, ...rest } = booking

    return {
      id,
      ...rest,
      directionality,
      transportMeans,
      tripSteps: [step],
      departureCoachCompany:
        step.directionality === TripStepTypeEnum.INBOUND ? coachCompany : undefined,
      arrivalCoachCompany:
        step.directionality === TripStepTypeEnum.OUTBOUND ? coachCompany : undefined,
      travelCompany
    }
  }

  const result: TripBookingTypes.View[] = []

  for (const booking of bookings) {
    const { tripSteps, directionality, departureCoachCompany, arrivalCoachCompany } = booking

    const inboundSteps = tripSteps.filter(
      (step) => step.directionality === TripStepTypeEnum.INBOUND
    )
    const outboundSteps = tripSteps.filter(
      (step) => step.directionality === TripStepTypeEnum.OUTBOUND
    )

    if (
      directionality === TransportTypeEnum.ONE_WAY ||
      directionality === TransportTypeEnum.ROUND_TRIP
    ) {
      for (const step of inboundSteps) {
        result.push(wrapStep(booking, step, departureCoachCompany))
      }
    }

    if (
      directionality === TransportTypeEnum.RETURN ||
      directionality === TransportTypeEnum.ROUND_TRIP
    ) {
      for (const step of outboundSteps) {
        result.push(wrapStep(booking, step, arrivalCoachCompany))
      }
    }
  }

  return removeDuplicatesFromTripSteps(result)
}

export const mapCenterCourseToInput = (centerCourse: CenterCourse): CenterCourseInput => {
  return {
    date: centerCourse.date,
    startingTime: {
      hours: new Date(centerCourse.startingTime).getHours(),
      minutes: new Date(centerCourse.startingTime).getMinutes()
    },
    endingTime: {
      hours: new Date(centerCourse.endingTime).getHours(),
      minutes: new Date(centerCourse.endingTime).getMinutes()
    }
  }
}

export const mapCenterActivityToInput = (centerActivity: CenterActivity): CenterActivityInput => {
  return {
    date: centerActivity.date,
    startingTime: {
      hours: new Date(centerActivity.startingTime).getHours(),
      minutes: new Date(centerActivity.startingTime).getMinutes()
    },
    endingTime: {
      hours: new Date(centerActivity.endingTime).getHours(),
      minutes: new Date(centerActivity.endingTime).getMinutes()
    },
    comment: centerActivity.comment,
    activityId: centerActivity.activityId,
    activityPaidBy: centerActivity.activityPaidBy,
    journeyIds: centerActivity.journeys?.map((journey) => journey.id) ?? [],
    optionId: centerActivity.optionId ?? null
  }
}
